import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useHttp from '../../../hooks/http.hook'
import './CartModal.css'
import { cartLineTotal, formatPeriodRange } from '../../../utils/rentalPeriod'

function CartModal({ isOpen, items, onIncrease, onDecrease, onRemove, onClose }) {
  const navigate = useNavigate()
  const { business } = useParams()
  const { requestWithMeta } = useHttp()
  const [activeOptionsKey, setActiveOptionsKey] = useState(null)
  const [promotionsOpen, setPromotionsOpen] = useState(false)
  const [promotions, setPromotions] = useState([])
  const [promotionsLoading, setPromotionsLoading] = useState(false)
  const [promotionsError, setPromotionsError] = useState(null)
  const inactivityTimerRef = useRef(null)

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
    }
  }, [])

  const scheduleAutoCloseOptions = useCallback(() => {
    clearInactivityTimer()
    inactivityTimerRef.current = setTimeout(() => {
      setActiveOptionsKey(null)
    }, 2000)
  }, [clearInactivityTimer])

  const bumpInactivity = useCallback(() => {
    if (activeOptionsKey) scheduleAutoCloseOptions()
  }, [activeOptionsKey, scheduleAutoCloseOptions])

  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) {
      setActiveOptionsKey(null)
      clearInactivityTimer()
      setPromotionsOpen(false)
      setPromotionsError(null)
      setPromotions([])
      setPromotionsLoading(false)
    }
  }, [isOpen, clearInactivityTimer])

  useEffect(() => {
    if (!isOpen || !business) return
    let cancelled = false
    setPromotionsLoading(true)
    setPromotionsError(null)
    setPromotions([])
    ;(async () => {
      try {
        const { ok, data, status } = await requestWithMeta(`/business/${encodeURIComponent(business)}/promotions`)
        if (cancelled) return
        if (!ok) {
          setPromotions([])
          setPromotionsError(status === 404 ? 'Бизнес не найден' : data?.error || 'Не удалось загрузить акции')
          return
        }
        setPromotions(Array.isArray(data?.promotions) ? data.promotions : [])
      } catch (e) {
        if (!cancelled) {
          setPromotionsError(e.message || 'Ошибка загрузки')
          setPromotions([])
        }
      } finally {
        if (!cancelled) setPromotionsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOpen, business, requestWithMeta])

  const openPromotionsSheet = useCallback(() => {
    if (!business || promotions.length === 0) return
    setPromotionsOpen(true)
  }, [business, promotions.length])

  const showGiftButton = Boolean(business && !promotionsLoading && !promotionsError && promotions.length > 0)
  const showFooterSkeleton = Boolean(business && promotionsLoading)

  const goToGiftSelection = useCallback(
    (promotionId) => {
      if (!business) return
      onClose?.()
      navigate(`/${encodeURIComponent(business)}/promotions/${encodeURIComponent(promotionId)}/gift`, {
        state: { cartItems: items || [] },
      })
    },
    [business, items, navigate, onClose]
  )

  useEffect(() => {
    if (!activeOptionsKey) {
      clearInactivityTimer()
      return
    }
    scheduleAutoCloseOptions()
    return clearInactivityTimer
  }, [activeOptionsKey, scheduleAutoCloseOptions, clearInactivityTimer])

  const totalItems = useMemo(
    () => (items || []).reduce((sum, it) => sum + (it.quantity || 0), 0),
    [items]
  )

  const totalPrice = useMemo(() => {
    return (items || []).reduce((sum, it) => sum + cartLineTotal(it), 0)
  }, [items])

  const handleBuy = useCallback(() => {
    if (!business) return
    const ids = (items || []).map((it) => it?.id).filter(Boolean)
    const qs = new URLSearchParams()
    ids.forEach((id) => qs.append('productIds', id))
    onClose?.()
    navigate(`/${encodeURIComponent(business)}/branches${qs.toString() ? `?${qs.toString()}` : ''}`, {
      state: { cartItems: items || [] },
    })
  }, [business, items, navigate, onClose])

  if (!isOpen) return null

  return (
    <div className="cart-modal-overlay" onMouseDown={() => onClose?.()} role="presentation">
      <div
        className="cart-modal"
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={bumpInactivity}
        role="dialog"
        aria-modal="true"
        aria-label="Cart"
      >
        <div className="cart-modal-header">
          <div className="cart-modal-title">
            Выбранные продукты
            <span className="cart-modal-subtitle">всего: {totalItems}</span>
            <span className="cart-modal-subtitle">итого: {totalPrice.toFixed(2)} сом</span>
          </div>
          <button className="cart-modal-close" type="button" onClick={() => onClose?.()} aria-label="Close cart">
            ×
          </button>
        </div>

        <div className="cart-modal-body">
          {(!items || items.length === 0) ? (
            <div className="cart-modal-empty">Корзина пуста.</div>
          ) : (
            <ul className="cart-modal-list">
              {items.map((it) => (
                <li key={it.key} className="cart-modal-item">
                  <div className="cart-modal-item-left">
                    {it.image ? (
                      <img className="cart-modal-item-image" src={it.image} alt={it.name} />
                    ) : (
                      <div className="cart-modal-item-image cart-modal-item-image--placeholder" />
                    )}
                    <div className="cart-modal-item-meta">
                      <div className="cart-modal-item-name">{it.name}</div>
                      {it.fromAt && it.toAt ? (
                        <div className="cart-modal-item-period">{formatPeriodRange(it.fromAt, it.toAt)}</div>
                      ) : null}
                      {typeof it.price === 'number' && (
                        <div className="cart-modal-item-price">
                          {(cartLineTotal(it) / (Number(it.quantity) || 1)).toFixed(2)} сом
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="cart-modal-item-right">
                    <div className="cart-modal-qty-pill" aria-label={`Quantity ${it.quantity}`}>
                      × {it.quantity}
                    </div>

                    <button
                      className="cart-modal-options-btn"
                      type="button"
                      onClick={() => {
                        setActiveOptionsKey((prev) => (prev === it.key ? null : it.key))
                      }}
                      aria-label={activeOptionsKey === it.key ? `Close options for ${it.name}` : `Open options for ${it.name}`}
                      title="Options"
                    >
                      <span aria-hidden="true">⋯</span>
                    </button>

                    <div
                      className={`cart-modal-actions ${activeOptionsKey === it.key ? 'open' : ''}`}
                      role="group"
                      aria-label={`Actions for ${it.name}`}
                      onPointerDown={bumpInactivity}
                    >
                      <div className="cart-modal-qty-controls" aria-label={`Quantity controls for ${it.name}`}>
                        <button
                          className="cart-modal-qty-btn"
                          type="button"
                          onClick={() => onDecrease?.(it.key)}
                          aria-label={`Decrease ${it.name}`}
                        >
                          –
                        </button>
                        <div className="cart-modal-qty-value" aria-label={`Quantity ${it.quantity}`}>
                          {it.quantity}
                        </div>
                        <button
                          className="cart-modal-qty-btn"
                          type="button"
                          onClick={() => onIncrease?.(it.key)}
                          aria-label={`Increase ${it.name}`}
                        >
                          +
                        </button>
                      </div>

                      <button
                        className="cart-modal-remove-btn"
                        type="button"
                        onClick={() => onRemove?.(it.key)}
                        aria-label={`Remove ${it.name}`}
                        title="Remove"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M10 12V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M14 12V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M4 7H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M6 10V18C6 19.6569 7.34315 21 9 21H15C16.6569 21 18 19.6569 18 18V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="cart-modal-footer">
          {showFooterSkeleton ? (
            <div className="cart-modal-footer-actions">
              <div className="cart-modal-footerSkeleton" aria-busy="true" aria-label="Загрузка" />
            </div>
          ) : (
            <div className="cart-modal-footer-actions">
              {showGiftButton && (
                <button
                  className="cart-modal-giftBtn"
                  type="button"
                  onClick={openPromotionsSheet}
                  aria-label="Подарки по акциям"
                  title="Подарки по акциям"
                >
                  <span aria-hidden="true">🎁</span>
                </button>
              )}
              <button
                className="cart-modal-buyBtn"
                type="button"
                disabled={!business || !items || items.length === 0}
                onClick={handleBuy}
              >
                Заказать
              </button>
            </div>
          )}
        </div>

        {promotionsOpen && (
          <div
            className="cart-modal-promo-overlay"
            role="presentation"
            onMouseDown={() => setPromotionsOpen(false)}
          >
            <div
              className="cart-modal-promo-sheet"
              onMouseDown={(e) => e.stopPropagation()}
              role="dialog"
              aria-label="Акции"
            >
              <div className="cart-modal-promo-sheetHeader">
                <div className="cart-modal-promo-sheetTitle">Акции</div>
                <button
                  type="button"
                  className="cart-modal-promo-close"
                  onClick={() => setPromotionsOpen(false)}
                  aria-label="Закрыть"
                >
                  ×
                </button>
              </div>
              <div className="cart-modal-promo-body">
                {promotionsLoading && <div className="cart-modal-promo-hint">Загрузка…</div>}
                {!promotionsLoading && promotionsError && (
                  <div className="cart-modal-promo-error">{promotionsError}</div>
                )}
                {!promotionsLoading && !promotionsError && promotions.length === 0 && (
                  <div className="cart-modal-promo-empty">Нет активных акций.</div>
                )}
                {!promotionsLoading && !promotionsError && promotions.length > 0 && (
                  <ul className="cart-modal-promo-list">
                    {promotions.map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          className="cart-modal-promo-row"
                          onClick={() => goToGiftSelection(p.id)}
                        >
                          <img className="cart-modal-promo-thumb" src={p.imageUrl || "https://gifs.obs.ru-moscow-1.hc.sbercloud.ru/ea1c4362ccb0338a0b33b137a6241a7569e28967c616e7e31d77a571ef351c92.gif"} alt="" />
                          <span className="cart-modal-promo-name">{p.name}</span>
                          <span className="cart-modal-promo-chevron" aria-hidden="true">
                            ›
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CartModal
